import { Button, useTranslation } from '@hbs-us/ui';
import { useMemo, useState } from 'react';
import type { QcChecklistDetail, QcSection } from './contractsMirror.js';
import { QcActions } from './QcActions.js';
import { QcItemRow } from './QcItemRow.js';
import { QcShareLink } from './QcShareLink.js';

/**
 * The checklist form.
 *
 * ~300 items on the full templates, so sections are collapsed and it opens on the FIRST UNFINISHED
 * one rather than at the top. Somebody resuming a walkthrough should land where they stopped, not
 * scroll past nine finished sections to get there.
 *
 * Progress is per section and shown in the header, because "how much is left" is the question
 * people actually have while holding a phone in an unfinished house.
 *
 * Outstanding — fail or needs-review, unwaived — is surfaced separately from unanswered. They are
 * different problems: unanswered is work not done, outstanding is work that failed. Only the second
 * will block sign-off.
 */

interface SectionProgress {
  answered: number;
  total: number;
  outstanding: number;
  /** Check items with no state. These are what block a submit. */
  unanswered: number;
  complete: boolean;
}

function progressOf(section: QcSection): SectionProgress {
  let answered = 0;
  let outstanding = 0;
  let unanswered = 0;
  for (const item of section.items) {
    const hasAnswer =
      item.itemType === 'check' ? item.state !== null : Boolean(item.valueText?.trim());
    if (hasAnswer) answered += 1;
    // Only CHECK items block a submit. A blank project-information field or an unspecified
    // selection is missing detail, not an uninspected item, and holding the whole checklist for
    // one would make the rule feel arbitrary.
    if (item.itemType === 'check' && item.state === null) unanswered += 1;
    if ((item.state === 'fail' || item.state === 'needs_review') && !item.waiver) outstanding += 1;
  }
  return {
    answered,
    total: section.items.length,
    outstanding,
    unanswered,
    complete: answered === section.items.length,
  };
}

export interface QcChecklistViewProps {
  data: QcChecklistDetail;
}

export function QcChecklistView({ data }: QcChecklistViewProps) {
  const { t } = useTranslation();
  const sections = data.sections ?? [];
  const canAnswer = data.access?.canAnswer ?? false;
  // Waiving is a sign-off decision, so it rides the same server flag as sign-off (`qc.approve`).
  // A signed-off checklist is locked and an archived one is retired, so neither offers a waive.
  const canWaive =
    (data.access?.canApprove ?? false) &&
    data.checklist?.status !== 'approved' &&
    data.checklist?.archivedAt === null;
  const checklist = data.checklist;

  const progress = useMemo(() => sections.map(progressOf), [sections]);

  const totals = useMemo(
    () =>
      progress.reduce(
        (acc, p) => ({
          answered: acc.answered + p.answered,
          total: acc.total + p.total,
          outstanding: acc.outstanding + p.outstanding,
          unanswered: acc.unanswered + p.unanswered,
        }),
        { answered: 0, total: 0, outstanding: 0, unanswered: 0 },
      ),
    [progress],
  );

  // Open the first section with work left in it. Falls back to the first section when everything is
  // done, so the page is never a wall of closed rows with nothing indicated.
  const initiallyOpen = useMemo(() => {
    const index = progress.findIndex((p) => !p.complete);
    return sections.length === 0 ? null : sections[index === -1 ? 0 : index].title;
  }, [progress, sections]);

  const [openSection, setOpenSection] = useState<string | null>(initiallyOpen);

  if (!checklist) return null;

  return (
    <div className="min-h-full bg-background px-4 py-6 sm:px-6 sm:py-8">
      <header className="mb-5 flex flex-col gap-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="font-display font-semibold text-2xl text-foreground sm:text-3xl">
              {checklist.name}
            </h1>
            <p className="mt-1 text-muted-foreground text-sm">
              {t('qc:detail.templateLine', {
                template: checklist.templateName,
                version: checklist.templateVersion,
              })}
            </p>
          </div>
          <span className="rounded-md bg-secondary px-2.5 py-1 font-medium text-secondary-foreground text-xs">
            {t(`qc:status.${checklist.status}`)}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          <span className="font-mono text-muted-foreground">
            {t('qc:detail.progress', { answered: totals.answered, total: totals.total })}
          </span>
          {totals.outstanding > 0 ? (
            // TEXT, not colour alone. This is the number that will stop sign-off.
            <span className="font-medium text-destructive">
              {t('qc:detail.outstanding', { count: totals.outstanding })}
            </span>
          ) : null}
          {!canAnswer ? (
            <span className="text-muted-foreground">{t('qc:detail.readOnly')}</span>
          ) : null}
        </div>
      </header>

      {/* A direct link to an archived checklist keeps working — one out of an email has to. It says
          what it is instead, because otherwise the only clue is its absence from a list the person
          following the link never looked at. */}
      {checklist.archivedAt !== null ? (
        <p
          role="status"
          className="mb-4 rounded-md border border-border bg-secondary px-4 py-3 text-secondary-foreground text-sm"
        >
          {t('qc:detail.archivedNotice')}
        </p>
      ) : null}

      {(data.fields ?? []).length > 0 ? (
        <section
          aria-label={t('qc:detail.jobDetails')}
          className="mb-4 rounded-md border border-border bg-card p-4"
        >
          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
            {(data.fields ?? [])
              .filter((f) => f.value)
              .map((field) => (
                <div key={field.key} className="flex min-w-0 flex-wrap gap-x-2">
                  <dt className="text-muted-foreground text-sm">{field.key}</dt>
                  <dd className="min-w-0 break-words text-foreground text-sm">{field.value}</dd>
                </div>
              ))}
          </dl>
        </section>
      ) : null}

      <div className="flex flex-col gap-3">
        {sections.map((section, index) => {
          const p = progress[index];
          const open = openSection === section.title;
          return (
            <section key={section.title} className="rounded-md border border-border bg-card">
              <h2>
                <button
                  type="button"
                  className="flex min-h-11 w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                  aria-expanded={open}
                  onClick={() => setOpenSection(open ? null : section.title)}
                >
                  <span className="min-w-0 flex-1 font-semibold text-foreground text-sm">
                    {section.title}
                  </span>
                  {p.outstanding > 0 ? (
                    <span className="rounded bg-destructive/10 px-1.5 py-0.5 font-medium text-destructive text-xs">
                      {t('qc:detail.sectionOutstanding', { count: p.outstanding })}
                    </span>
                  ) : null}
                  <span
                    className={`whitespace-nowrap font-mono text-xs ${
                      p.complete ? 'text-success' : 'text-muted-foreground'
                    }`}
                  >
                    {p.answered}/{p.total}
                  </span>
                </button>
              </h2>

              {open ? (
                <>
                  {section.description ? (
                    <p className="border-border border-t px-4 py-2 text-muted-foreground text-xs">
                      {section.description}
                    </p>
                  ) : null}
                  <ul className="border-border border-t">
                    {section.items.map((item) => (
                      <QcItemRow
                        key={item.id}
                        checklistId={checklist.id}
                        item={item}
                        canAnswer={canAnswer}
                        canWaive={canWaive}
                      />
                    ))}
                  </ul>
                </>
              ) : null}
            </section>
          );
        })}
      </div>

      {/* Archiving revokes the QR server-side, so offering to mint a fresh one for a retired
          checklist would hand somebody a code for work the office has taken off the list. */}
      {canAnswer && checklist.status !== 'approved' && checklist.archivedAt === null ? (
        <QcShareLink
          checklistId={checklist.id}
          checklistName={checklist.name}
          subtitle={t('qc:detail.templateLine', {
            template: checklist.templateName,
            version: checklist.templateVersion,
          })}
        />
      ) : null}

      <QcActions data={data} outstanding={totals.outstanding} unanswered={totals.unanswered} />

      {(data.assignees ?? []).length > 0 ? (
        <section aria-label={t('qc:detail.assignees')} className="mt-4">
          <p className="text-muted-foreground text-xs">
            {t('qc:detail.assignedTo', {
              names: (data.assignees ?? []).map((a) => a.name).join(', '),
            })}
          </p>
        </section>
      ) : null}
    </div>
  );
}
